/*
 *
 *  * (C) Copyright 2006-2020 hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * All Rights Reserved.
 *  *
 *  * These materials are unpublished, proprietary, confidential source code of
 *  * hSenid Mobile Solutions (Pvt) Limited and constitute a TRADE SECRET
 *  * of hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * hSenid Mobile Solutions (Pvt) Limited retains all title to and intellectual
 *  * property rights in these materials.
 *  *
 *
 */

import produce from "immer";
import { call, put, takeLatest } from "redux-saga/effects";
import { getMyApps, MyApp } from "../../../api/apiCalls";
import {
  LoadingStatus,
  usePortalStateSelector,
} from "../../../rootReducer/actions";
import { MainPageEvent } from "../../MainUi";

export interface MyAppsReq {
  senderAddress: string;
}

export enum MyAppsPageEvent {
  RESET = "RESET",
  MY_APPS_PAGE_DATA = "MY_APPS_PAGE_DATA",
  MY_APPS_PAGE_DATA_RESULT = "MY_APPS_PAGE_DATA_RESULT",
  MY_APPS_PAGE_DATA_FAIL = "MY_APPS_PAGE_DATA_FAIL",
}

export interface GetMyAppsPageAction {
  type: MyAppsPageEvent.MY_APPS_PAGE_DATA;
  payload: MyAppsReq;
  token: string;
}

export interface GetMyAppsPageResultsAction {
  type: MyAppsPageEvent.MY_APPS_PAGE_DATA_RESULT;
  results: {
    payload: MyApp[];
  };
}

export interface ResetMyAppsPageResults {
  type: MyAppsPageEvent.RESET;
}

export interface GetMyAppsPageActionFail {
  type: MyAppsPageEvent.MY_APPS_PAGE_DATA_FAIL;
}

export type MyAppsPageAction =
  | ResetMyAppsPageResults
  | GetMyAppsPageAction
  | GetMyAppsPageResultsAction
  | GetMyAppsPageActionFail;

export interface MyAppsPageData {
  loadingStatus: LoadingStatus;
  apps: MyApp[];
}

const initialState = {
  loadingStatus: LoadingStatus.LOADING_STARTED,
  notification: {
    show: false,
  },
  apps: [],
};

/**
 * Reducer
 */
export function myAppsPageReducer(
  state: MyAppsPageData = initialState,
  action: MyAppsPageAction
) {
  return produce(state, (draft) => {
    switch (action.type) {
      case MyAppsPageEvent.MY_APPS_PAGE_DATA: {
        break;
      }

      case MyAppsPageEvent.MY_APPS_PAGE_DATA_RESULT: {
        draft.loadingStatus = LoadingStatus.LOADING_SUCCESS;
        if (action.results.payload) {
          draft.apps = draft.apps?.concat(action.results.payload);
        }
        break;
      }

      case MyAppsPageEvent.MY_APPS_PAGE_DATA_FAIL: {
        break;
      }

      case MyAppsPageEvent.RESET: {
        draft.apps = [];
        break;
      }
    }
  });
}

/**
 * Selector
 */

export function useMyAppsPageDataSelector(): MyAppsPageData {
  return usePortalStateSelector<MyAppsPageData>((ev) => ev.myAppsPageData);
}

/**
 * Page Saga
 */

export const myAppsPageSagas = [getMyAppsPageDataSaga];

/**
 * Watcher Sagas
 */
function* getMyAppsPageDataSaga() {
  yield takeLatest(MyAppsPageEvent.MY_APPS_PAGE_DATA, callMyAppsPageData);
}

/**
 * Handler functions
 */

const arr = ["E1008", "E1009", "E1010", "E1011", "E1012", "E1013"];

function* callMyAppsPageData(action: GetMyAppsPageAction) {
  try {
    const { payload, debugMessage, statusCode } = yield call(
      getMyApps,
      action.payload,
      action.token
    );
    if (statusCode === "S1000") {
      yield put({
        type: MyAppsPageEvent.MY_APPS_PAGE_DATA_RESULT,
        results: payload,
      });
    } else if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      window.location.reload();
    } else {
      yield put({
        type: MyAppsPageEvent.MY_APPS_PAGE_DATA_RESULT,
        results: [],
      });
    }
    yield put({
      type: MyAppsPageEvent.MY_APPS_PAGE_DATA_RESULT,
      results: { payload: statusCode === "S1000" ? payload : [] },
    });
  } catch (e) {
    console.error(e);
    yield put({ type: MyAppsPageEvent.MY_APPS_PAGE_DATA_FAIL });
  }
}
